import type { RtdbClient } from '@akpl/firebase/client';
import {
  type ClosedReason,
  MembersNode,
  PlayerNode,
  QueueNode,
  rtdbPaths,
  SessionMeta,
} from '@akpl/shared';

import type { TvStoreApi } from '../store';

export interface SubscribeSessionOptions {
  /**
   * Called once `meta.status` becomes `closed` (docs/04 §5 session expiry), with the reason the
   * API wrote next to it (QA 2026-09-15 UX-566) — `null` for a session closed by a deploy that
   * did not write one.
   */
  onSessionClosed?: (reason: ClosedReason | null) => void;
  /** Schema mismatch or a permission error on one of the four nodes. */
  onNodeError?: (path: string, error: Error) => void;
}

/**
 * Subscribes the store to the live session document (docs/02 §2): `meta`, `queue`, `player`
 * and `members`. Each node is validated with its zod schema before it reaches a slice, so a
 * malformed write can never corrupt the screens — it only leaves the slice at its last value
 * (`null` / `{}`) and reports through `onNodeError`.
 *
 * Framework-free on purpose: `useSessionLive` wraps it for the shell and the emulator test
 * drives it directly with a real `Database`.
 */
export function subscribeSession(
  client: RtdbClient,
  sid: string,
  store: TvStoreApi,
  options: SubscribeSessionOptions = {},
): () => void {
  const fail = (path: string) => (error: Error) => options.onNodeError?.(path, error);

  const metaPath = rtdbPaths.meta(sid);
  const queuePath = rtdbPaths.queue(sid);
  const playerPath = rtdbPaths.player(sid);
  const membersPath = rtdbPaths.members(sid);

  const unsubscribes = [
    client.listen(
      metaPath,
      SessionMeta,
      (meta) => {
        store.getState().setMeta(meta);
        if (meta?.status === 'closed') options.onSessionClosed?.(meta.closedReason ?? null);
      },
      fail(metaPath),
    ),
    client.listen(
      queuePath,
      QueueNode,
      (queue) => store.getState().setQueue(queue),
      fail(queuePath),
    ),
    client.listen(
      playerPath,
      PlayerNode,
      (player) => store.getState().setRemotePlayer(player),
      fail(playerPath),
    ),
    client.listen(
      membersPath,
      MembersNode,
      (members) => store.getState().setMembers(members),
      fail(membersPath),
    ),
  ];

  return () => {
    for (const unsubscribe of unsubscribes) unsubscribe();
  };
}
